import * as React from 'react';

import {SaveButton} from 'shells/button/save-button/save-button';
import {SegmentedControl} from 'shells/card/segmented-control';
import {CodeBlock} from 'shells/code-block/code-block';
import {FormField} from 'shells/form/form-field';
import {Stack} from 'shells/layout/stack/stack';
import {TextField} from 'shells/textfield/textfield';
import {Body} from 'shells/typography/body';
import {UserSelectButton} from 'shells/user-select-button/user-select-button';

import type {GetUsers_users_edges_node as User} from 'nutshell-graphql-types';

import {useGetUsers} from '../core/select/hooks';

import {
    DIGEST_TYPE_MORNING_DIGEST,
    DIGEST_TYPE_OPTIONS,
    FREQUENCY_DAILY,
    FREQUENCY_OPTIONS,
} from './consts';
import type {DigestType, Frequency} from './consts';
import {fetchDigest, sendTestEmail, sendDigestViaGearman} from './helpers';
import type {DigestResponse, ErrorDetails, SendEmailResponse} from './helpers';
import {RenderErrorBanner} from './render-error-banner';

const ANDY_USER_ID = '1-users';

export function EmailDigestPage() {
    const [selectedUser, setSelectedUser] = React.useState<User | null | undefined>(null);
    const [selectedDigestType, setSelectedDigestType] = React.useState<DigestType>(
        DIGEST_TYPE_MORNING_DIGEST
    );
    const [selectedFrequency, setSelectedFrequency] = React.useState<Frequency>(FREQUENCY_DAILY);
    const [currentDigest, setCurrentDigest] = React.useState<string | null | undefined>(null);
    const [currentDigestInput, setCurrentDigestInput] = React.useState<string>('');
    const [renderError, setRenderError] = React.useState<string | null | undefined>(null);
    const [renderErrorDetails, setRenderErrorDetails] = React.useState<
        ErrorDetails | null | undefined
    >(null);
    const [fetchError, setFetchError] = React.useState<string | null | undefined>(null);

    const [testEmailAddress, setTestEmailAddress] = React.useState<string>('');
    const [isSendingTestEmail, setIsSendingTestEmail] = React.useState<boolean>(false);
    const [testEmailError, setTestEmailError] = React.useState<string | null | undefined>(null);

    const [isSendingViaGearman, setIsSendingViaGearman] = React.useState<boolean>(false);
    const [sendingViaGearmanError, setSendingViaGearmanError] = React.useState<
        string | null | undefined
    >(null);

    const {users} = useGetUsers();

    React.useEffect(() => {
        if (users.length > 0 && !selectedUser) {
            setSelectedUser(users.find((user) => user.id === ANDY_USER_ID));
        }
    }, [selectedUser, users]);

    React.useEffect(() => {
        if (!selectedUser) {
            return;
        }

        // Reset errors when fetching new digest
        setRenderError(null);
        setRenderErrorDetails(null);
        setFetchError(null);
        setCurrentDigest(null);
        setSendingViaGearmanError(null);

        fetchDigest(selectedDigestType, selectedFrequency, selectedUser.id)
            .then((response: DigestResponse) => {
                setCurrentDigest(response.htmlContent);
                setCurrentDigestInput(response.inputJson);
                setRenderError(response.error);
                setRenderErrorDetails(response.errorDetails);
            })
            .catch((error: Error) => {
                setFetchError(error.message);
            });
    }, [selectedDigestType, selectedFrequency, selectedUser]);

    const handleSendTestEmail = () => {
        if (!selectedUser || !testEmailAddress) {
            return;
        }

        setIsSendingTestEmail(true);
        setTestEmailError(null);

        sendTestEmail(selectedDigestType, selectedFrequency, selectedUser.id, testEmailAddress)
            .then((response: SendEmailResponse) => {
                if (!response.success) {
                    setTestEmailError(response.error || 'Failed to send test email');
                }
            })
            .catch((error: Error) => {
                setTestEmailError(error.message);
            })
            .finally(() => {
                setIsSendingTestEmail(false);
            });
    };

    const handleSendDigestViaGearman = () => {
        if (!selectedUser) {
            return;
        }

        setIsSendingViaGearman(true);
        setSendingViaGearmanError(null);

        sendDigestViaGearman(selectedDigestType, selectedUser.id)
            .then((response: SendEmailResponse) => {
                if (!response.success) {
                    setSendingViaGearmanError(
                        response.error || 'Failed to send digest via Gearman'
                    );
                }
            })
            .catch((error: Error) => {
                setSendingViaGearmanError(error.message);
            })
            .finally(() => {
                setIsSendingViaGearman(false);
            });
    };

    return (
        <div className='pad-16'>
            <Stack spacing={16}>
                <Stack spacing={48} isHorizontal={true}>
                    <Stack spacing={16}>
                        <Body>Email Digest</Body>
                        <UserSelectButton
                            selectedUserId={selectedUser ? selectedUser.id : null}
                            onSelect={(userId?: string | null) =>
                                setSelectedUser(users.find((user) => user.id === userId))
                            }
                            users={users}
                        />
                        <div
                            className='flex flex-dir-col gap-16'
                            style={{width: '300px', height: '100px'}}
                        >
                            <SegmentedControl
                                buttonOptions={DIGEST_TYPE_OPTIONS}
                                selectedValue={selectedDigestType}
                                setSelectedValue={setSelectedDigestType}
                                isAnimated={true}
                            />
                            <SegmentedControl
                                buttonOptions={FREQUENCY_OPTIONS}
                                selectedValue={selectedFrequency}
                                setSelectedValue={setSelectedFrequency}
                                isAnimated={true}
                            />
                        </div>
                        <div style={{marginTop: '16px'}}>
                            <FormField innerId='test-email' label='Send test email to mailpit'>
                                <div className='flex gap-8 align-center'>
                                    <div style={{width: '250px'}}>
                                        <TextField
                                            // @ts-expect-error
                                            name='test-email'
                                            placeholder='test@example.com'
                                            type='email'
                                            value={testEmailAddress}
                                            onChange={(value) => {
                                                setTestEmailAddress(value);
                                                setTestEmailError(null);
                                            }}
                                        />
                                    </div>
                                    <SaveButton
                                        // @ts-expect-error
                                        onClick={handleSendTestEmail}
                                        disabled={!selectedUser || !testEmailAddress}
                                        isSaving={isSendingTestEmail}
                                        errorSaving={Boolean(testEmailError)}
                                        resetOnComplete={true}
                                        saveCompleteTimeout={2000}
                                        buttonText='Send test'
                                        savingText='Sending…'
                                        successText='Sent!'
                                    />
                                </div>
                            </FormField>
                            {testEmailError && (
                                <div
                                    style={{
                                        color: '#dc2626',
                                        fontSize: '12px',
                                        marginTop: '4px',
                                    }}
                                >
                                    {testEmailError}
                                </div>
                            )}
                        </div>
                        <div style={{marginTop: '16px'}}>
                            <FormField
                                innerId='send-digest-via-gearman'
                                label='Send digest via Gearman'
                            >
                                <div className='flex gap-8 align-center'>
                                    <SaveButton
                                        // @ts-expect-error
                                        onClick={handleSendDigestViaGearman}
                                        disabled={!selectedUser}
                                        isSaving={isSendingViaGearman}
                                        errorSaving={Boolean(sendingViaGearmanError)}
                                        resetOnComplete={true}
                                        saveCompleteTimeout={2000}
                                        buttonText='Send with Gearman'
                                        savingText='Sending…'
                                        successText='Sent!'
                                    />
                                </div>
                            </FormField>
                            {sendingViaGearmanError && (
                                <div
                                    style={{
                                        color: '#dc2626',
                                        fontSize: '12px',
                                        marginTop: '4px',
                                    }}
                                >
                                    {sendingViaGearmanError}
                                </div>
                            )}
                        </div>
                    </Stack>
                    {currentDigestInput && (
                        <div style={{width: '700px'}}>
                            <CodeBlock
                                value={JSON.stringify(JSON.parse(currentDigestInput), null, 2)}
                                includeCopyButton={true}
                                name='digest-input'
                                label='Digest Input'
                            />
                        </div>
                    )}
                </Stack>
                {fetchError && (
                    <div
                        style={{
                            backgroundColor: '#fee2e2',
                            border: '2px solid #dc2626',
                            borderRadius: '8px',
                            padding: '16px',
                            color: '#991b1b',
                        }}
                    >
                        <strong>Fetch Error:</strong> {fetchError}
                    </div>
                )}
                {renderError && (
                    <RenderErrorBanner error={renderError} errorDetails={renderErrorDetails} />
                )}
                {currentDigest && <div dangerouslySetInnerHTML={{__html: currentDigest}} />}
            </Stack>
        </div>
    );
}
